import cx from 'classnames';
import kebabCase from 'lodash/kebabCase';
import {useCallback, useEffect, useRef, useState} from 'react';
import {HamburgerMenu} from '~/common/components/MegaNav/MegaNavVariant/components/HamburgerMenu/HamburgerMenu';
import {MegaNavVariantStoreProvider} from '~/common/components/MegaNav/MegaNavVariant/components/StoreProvider';
import {useGetCategories} from '~/common/components/MegaNav/MegaNavVariant/hooks/useGetCategories';
import type {
  CategoryPositionType,
  InvitationCategoryType,
} from '~/common/components/MegaNav/MegaNavVariant/types';
import {Divider} from '~library/atoms/Divider';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {SearchContextParent} from '~library/organisms/MegaNav/SearchWithCategory/SearchContext';
import {Categories} from './components/Categories/Categories';
import {Dropdown} from './components/Dropdown/Dropdown';
import {Search} from './components/Search/Search';
import * as styles from './LocalNav.module.scss';

export const LocalNav = () => {
  const [hoveredCategory, setHoveredCategory] = useState<InvitationCategoryType | null>(null);
  const [categoryPosition, setCategoryPosition] = useState<CategoryPositionType | null>(null);
  const [isMegaNavOpen, setIsMegaNavOpen] = useState(false);
  const [isAtTop, setIsAtTop] = useState(false);
  const [isSearchWithCategoryVisible, setIsSearchWithCategoryVisible] = useState(false);
  const {categories, currentGlobalNav} = useGetCategories();
  const localNavRef = useRef<HTMLDivElement | null>(null);

  const isInvitationPage = window.location.pathname === '/';

  const hideSearch = isInvitationPage && isSearchWithCategoryVisible;
  const isLargishAndUp = useIsLargishAndUp();

  const handleCategoryHover = useCallback(
    (category: InvitationCategoryType, position: CategoryPositionType): void => {
      setHoveredCategory(category);
      setCategoryPosition(position);
      setIsMegaNavOpen(true);
    },
    []
  );

  const handleCategoryLeave = useCallback((): void => {
    setIsMegaNavOpen(false);
    setHoveredCategory(null);
    setCategoryPosition(null);
  }, []);

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      const target = event.target as Node;
      const megaNavElement = localNavRef?.current;

      if (megaNavElement && !megaNavElement.contains(target)) {
        handleCategoryLeave();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseout', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [handleCategoryLeave]);

  useEffect(() => {
    const handleScroll = () => {
      if (localNavRef.current) {
        const rect = localNavRef.current.getBoundingClientRect();
        const newIsAtTop = rect.top <= 1;
        setIsAtTop(newIsAtTop);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkSearchWithCategoryVisibility = () => {
      const searchWithCategoryElement = document.getElementById('search-with-category');

      if (!searchWithCategoryElement) {
        setIsSearchWithCategoryVisible(false);
        return;
      }

      const rect = searchWithCategoryElement.getBoundingClientRect();
      const isVisible =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth;

      setIsSearchWithCategoryVisible(isVisible);
    };

    checkSearchWithCategoryVisibility();

    const observer = new MutationObserver(checkSearchWithCategoryVisibility);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    window.addEventListener('scroll', checkSearchWithCategoryVisibility);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', checkSearchWithCategoryVisibility);
    };
  }, []);

  useEffect(() => {
    if (localNavRef.current) {
      const localNavHeight = localNavRef.current.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--scrollbar-height', `${localNavHeight}px`);
    }
  }, []);

  const onSearchOpenCloseHandler = useCallback((value: boolean) => {
    const searchInputEl = document.querySelector('.search-with-category');

    if (searchInputEl) {
      const searchInputStyles = window.getComputedStyle(searchInputEl);
      const searchInputMaxWidth = searchInputStyles.maxWidth;

      const maxWidthPx =
        searchInputMaxWidth === 'none' ? Infinity : parseFloat(searchInputMaxWidth);

      const currentWidth = searchInputEl.getBoundingClientRect().width;

      const isShrunk = currentWidth < maxWidthPx;

      // Directly manipulate DOM to avoid React re-render conflicts
      if (localNavRef.current) {
        if (value && isShrunk) {
          localNavRef.current.classList.add(styles.localNavWithSearchOpen);
        } else {
          localNavRef.current.classList.remove(styles.localNavWithSearchOpen);
        }
      }
    }
  }, []);

  return (
    <SearchContextParent>
      {isLargishAndUp ? (
        <>
          <Divider />
          <div
            className={cx(styles.localNav, currentGlobalNav, {
              [styles.localNavWithSearch]: !hideSearch,
            })}
            ref={localNavRef}
          >
            <Categories
              categories={categories}
              hoveredCategory={hoveredCategory}
              handleCategoryHover={handleCategoryHover}
            />

            <Search hide={hideSearch} onSearchOpenClose={onSearchOpenCloseHandler} />

            <Dropdown
              hoveredCategory={hoveredCategory}
              categoryPosition={categoryPosition}
              alignDropdownToLeft={
                Boolean(hoveredCategory?.dropdownLeftAligned) || currentGlobalNav !== 'invitations'
              }
              isMegaNavOpen={isMegaNavOpen}
              onClickOutside={handleCategoryLeave}
              localNavRef={localNavRef}
              key={kebabCase(hoveredCategory?.title)}
            />
          </div>
          <Divider />
        </>
      ) : (
        <MegaNavVariantStoreProvider>
          <div
            ref={localNavRef}
            className={cx(styles.mobileSearchContainer, {
              [styles.mobileSearchContainerExtraPadding]: !hideSearch,
            })}
          >
            <Search className={styles.mobileSearchInput} />

            {isAtTop && <HamburgerMenu />}
          </div>
          <Divider />
        </MegaNavVariantStoreProvider>
      )}
    </SearchContextParent>
  );
};
